import React, { useMemo } from 'react';
import './Header.css';
import Countdown from 'react-countdown';
import { Link } from 'gatsby';
import { splitByPipe, formatBannerSegment, hasLineBreakMarker } from '../../utils';
import { usePromoCountdown } from '../../hooks/usePromoCountdown';
import { useHasMounted } from '../../hooks/useHasMounted';

const Timer = ({ days, hours, minutes, seconds }) => {
    const numberFormatter = new Intl.NumberFormat('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false,
    });
    days = numberFormatter.format(days);
    hours = numberFormatter.format(hours);
    minutes = numberFormatter.format(minutes);
    seconds = numberFormatter.format(seconds);

    return (
        <div className="row justify-content-center gx-4 gx-md-5 black-friday-timer">
            <div className="col-auto time-container px-1">
                <time className="d-block timer-number fw-semibold" dateTime={`P${days}D`}>
                    {days}
                </time>
                <span className="d-block timer-label">Days</span>
            </div>
            <span className="col-auto pt-1 px-0 fw-semibold">:</span>
            <div className="col-auto time-container px-1">
                <time className="d-block timer-number fw-semibold" dateTime={`PT${hours}H`}>
                    {hours}
                </time>
                <span className="d-block timer-label">Hrs</span>
            </div>
            <span className="col-auto pt-1 px-0 fw-semibold">:</span>
            <div className="col-auto time-container px-1">
                <time className="d-block timer-number fw-semibold" dateTime={`PT${minutes}M`}>
                    {minutes}
                </time>
                <span className="d-block timer-label">Min</span>
            </div>
            <span className="col-auto pt-1 px-0 fw-semibold">:</span>
            <div className="col-auto time-container px-1">
                <time className="d-block timer-number fw-semibold" dateTime={`PT${seconds}S`}>
                    {seconds}
                </time>
                <span className="d-block timer-label">Sec</span>
            </div>
        </div>
    );
};

// An internal Gatsby path starts with '/' but not '//' (which would be
// protocol-relative, so an external host). Everything else — absolute URLs,
// mailto, tel, protocol-relative — uses a plain <a>.
const isInternalPath = (href) => href?.startsWith('/') && !href.startsWith('//');

// Classes map to Bootstrap flex + text utilities so the banner can left/center/right
// align per the Builder entry. Center keeps the long-standing layout.
const ALIGNMENT_CLASSES = {
    left: 'align-items-start text-start',
    center: 'align-items-center text-center',
    right: 'align-items-end text-end',
};

// Rendered only where useCountdownActive() is true. The placeholder (hidden Timer)
// holds the banner's space until mount, since the timer itself can't be
// server-rendered.
function CountdownBanner() {
    const { countdown } = usePromoCountdown();
    const hasMounted = useHasMounted();

    // react-countdown restarts whenever it receives a new Date instance.
    const endDate = useMemo(() => (countdown ? new Date(countdown.endMs) : null), [countdown]);

    if (!countdown) return null;

    const { promoText, linkTo, backgroundColor, textAlign } = countdown;
    const { specialsBannerLinkTextFirstPart: promoLeadText, specialsBannerTextSecondPart: promoDetailText } =
        splitByPipe(promoText);
    const showLineBreak = hasLineBreakMarker(promoText);
    const alignmentClasses = ALIGNMENT_CLASSES[textAlign] || ALIGNMENT_CLASSES.center;

    const content = (
        <section
            id="black-friday-countdown"
            className={`d-flex ${alignmentClasses} justify-content-center flex-column py-3 px-3`}
            style={{ backgroundColor }}
        >
            <span className="countdown-text">
                <strong>{formatBannerSegment(promoLeadText, showLineBreak)}</strong>{' '}
                <span>{formatBannerSegment(promoDetailText, showLineBreak)}</span>
            </span>
            {hasMounted ? (
                <Countdown date={endDate} renderer={Timer} />
            ) : (
                <div style={{ visibility: 'hidden' }}>
                    <Timer days="00" hours="00" minutes="00" seconds="00" />
                </div>
            )}
        </section>
    );

    return isInternalPath(linkTo) ? (
        <Link to={linkTo} aria-label="View special offers and promotions">
            {content}
        </Link>
    ) : (
        <a href={linkTo} aria-label="View special offers and promotions">
            {content}
        </a>
    );
}

export default CountdownBanner;
